import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../hooks/useTheme";
import PlanFormModal from "./PlanFormModal";

const WHATSAPP_BASE =
	"https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20gustar%C3%ADa%20el%20";

function AnimatedPrice({
	price,
	isLight,
}: {
	price: string;
	isLight: boolean;
}) {
	const [display, setDisplay] = useState(price);
	const [animating, setAnimating] = useState(false);
	const prev = useRef(price);

	useEffect(() => {
		if (prev.current !== price) {
			setAnimating(true);
			const timer = setTimeout(() => {
				setDisplay(price);
				setAnimating(false);
				prev.current = price;
			}, 180);
			return () => clearTimeout(timer);
		}
	}, [price]);

	return (
		<span
			className="font-heading text-3xl md:text-4xl font-bold leading-none inline-block"
			style={{
				color: isLight ? "#0a1628" : "#ffffff",
				opacity: animating ? 0 : 1,
				transform: animating ? "translateY(-8px)" : "translateY(0)",
				transition: "opacity 0.18s ease, transform 0.18s ease",
			}}
		>
			${display}
		</span>
	);
}

interface PlanDef {
	id: string;
	name: string;
	taglineKey: string;
	price: string;
	implPrice: string;
	badge: string | null;
	accentHex: string;
	accentDark: string;
	limitValues: {
		users: string;
		cajas: string;
		branches: string;
	};
	support: {
		responseKey: string;
		channelsKey: string;
		includesKey: string;
	};
	features: { textKey: string; included: boolean }[];
	ctaKey: string;
	waText: string;
	priceOnRequest?: boolean;
}

type CompareCell = "check" | "addon" | "dash";

// Support block shown on every plan card
const SUPPORT_ROWS: {
	icon: string;
	labelKey: string;
	field: "responseKey" | "channelsKey" | "includesKey";
}[] = [
	{
		icon: "ri-timer-flash-line",
		labelKey: "support_row_response",
		field: "responseKey",
	},
	{
		icon: "ri-chat-3-line",
		labelKey: "support_row_channels",
		field: "channelsKey",
	},
	{
		icon: "ri-check-double-line",
		labelKey: "support_row_includes",
		field: "includesKey",
	},
];

// The headline limits shown on every card and as the first rows of the table
const LIMIT_META: {
	icon: string;
	labelKey: string;
	field: keyof PlanDef["limitValues"];
}[] = [
	{ icon: "ri-group-line", labelKey: "compare_lim_users", field: "users" },
	{ icon: "ri-computer-line", labelKey: "compare_lim_cajas", field: "cajas" },
	{
		icon: "ri-store-2-line",
		labelKey: "compare_lim_branches",
		field: "branches",
	},
];

// Simple comparison table — feature rows (limits are derived from LIMIT_META)
const COMPARE_FEATURE_ROWS: {
	labelKey: string;
	cells: Record<string, CompareCell>;
}[] = [
	{
		labelKey: "crow_inventory_adv",
		cells: {
			starter: "dash",
			negocio: "dash",
			pro: "check",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_accounting",
		cells: {
			starter: "dash",
			negocio: "dash",
			pro: "check",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_offline",
		cells: {
			starter: "check",
			negocio: "check",
			pro: "check",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_bulk_msg",
		cells: {
			starter: "dash",
			negocio: "dash",
			pro: "dash",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_ai",
		cells: {
			starter: "addon",
			negocio: "addon",
			pro: "addon",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_support_priority",
		cells: {
			starter: "dash",
			negocio: "dash",
			pro: "check",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_support_critical",
		cells: {
			starter: "dash",
			negocio: "dash",
			pro: "dash",
			premium: "check",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_integrations_api",
		cells: {
			starter: "addon",
			negocio: "addon",
			pro: "addon",
			premium: "addon",
			personalizado: "check",
		},
	},
	{
		labelKey: "crow_custom_dev",
		cells: {
			starter: "dash",
			negocio: "dash",
			pro: "dash",
			premium: "addon",
			personalizado: "check",
		},
	},
];

export default function Pricing() {
	const { theme } = useTheme();
	const isLight = theme === "light";
	const { t } = useTranslation();
	const [annual, setAnnual] = useState(false);
	const [visible, setVisible] = useState(false);
	const [showCompare, setShowCompare] = useState(false);
	const [modalPlan, setModalPlan] = useState<{
		name: string;
		color: string;
	} | null>(null);
	const sectionRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setVisible(true);
			},
			{ threshold: 0.1 },
		);
		if (sectionRef.current) observer.observe(sectionRef.current);
		return () => observer.disconnect();
	}, []);

	// Annual: show the full-year total (monthly − $3.99) × 12. Monthly: the monthly price.
	const getPrice = (base: string) =>
		annual
			? (Math.max(0, parseFloat(base) - 3.99) * 12).toLocaleString("en-US", {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})
			: base;

	const handleSelectPlan = (plan: PlanDef, color: string) => {
		if (plan.priceOnRequest) {
			window.open(
				`${WHATSAPP_BASE}${plan.waText}`,
				"_blank",
				"noopener,noreferrer",
			);
			return;
		}
		setModalPlan({ name: plan.name, color });
	};

	const planDefs: PlanDef[] = [
		{
			id: "starter",
			name: "Starter",
			taglineKey: "plan_starter_tagline",
			price: "29.99",
			implPrice: "$200",
			badge: null,
			accentHex: "#06b6d4",
			accentDark: "#0e7490",
			ctaKey: "plan_starter_cta",
			waText: "Plan%20Starter",
			limitValues: { users: "1", cajas: "1", branches: "1" },
			support: {
				responseKey: "support_starter_response",
				channelsKey: "support_starter_channels",
				includesKey: "support_starter_includes",
			},
			features: [
				{ textKey: "pf_billing_pos", included: true },
				{ textKey: "pf_clients_products", included: true },
				{ textKey: "pf_inventory_basic", included: true },
				{ textKey: "pf_dashboard_basic", included: true },
				{ textKey: "pf_reports_simple", included: true },
				{ textKey: "pf_offline_operational", included: true },
			],
		},
		{
			id: "negocio",
			name: "Business",
			taglineKey: "plan_business_tagline",
			price: "59.99",
			implPrice: "$200",
			badge: "pricing_badge_popular",
			accentHex: "#0ea5e9",
			accentDark: "#0369a1",
			ctaKey: "plan_business_cta",
			waText: "Plan%20Business",
			limitValues: { users: "3", cajas: "2", branches: "1" },
			support: {
				responseKey: "support_business_response",
				channelsKey: "support_business_channels",
				includesKey: "support_business_includes",
			},
			features: [
				{ textKey: "pf_includes_starter", included: true },
				{ textKey: "pf_purchases_suppliers", included: true },
				{ textKey: "pf_ar_ap_basic", included: true },
				{ textKey: "pf_low_stock_alerts", included: true },
				{ textKey: "pf_price_lists_2", included: true },
				{ textKey: "pf_offline_operational", included: true },
			],
		},
		{
			id: "pro",
			name: "Pro",
			taglineKey: "plan_pro_tagline",
			price: "99.99",
			implPrice: "$250",
			badge: null,
			accentHex: "#6366f1",
			accentDark: "#4338ca",
			ctaKey: "plan_pro_cta",
			waText: "Plan%20Pro",
			limitValues: { users: "5", cajas: "4", branches: "2" },
			support: {
				responseKey: "support_pro_response",
				channelsKey: "support_pro_channels",
				includesKey: "support_pro_includes",
			},
			features: [
				{ textKey: "pf_includes_business", included: true },
				{ textKey: "pf_inventory_advanced", included: true },
				{ textKey: "pf_accounting_operational", included: true },
				{ textKey: "pf_smart_alerts", included: true },
				{ textKey: "pf_offline_operational", included: true },
			],
		},
		{
			id: "premium",
			name: "Premium",
			taglineKey: "plan_premium_tagline",
			price: "149.99",
			implPrice: "$350",
			badge: "pricing_badge_complete",
			accentHex: "#a855f7",
			accentDark: "#7c3aed",
			ctaKey: "plan_premium_cta",
			waText: "Plan%20Premium",
			limitValues: { users: "9", cajas: "6", branches: "3" },
			support: {
				responseKey: "support_premium_response",
				channelsKey: "support_premium_channels",
				includesKey: "support_premium_includes",
			},
			features: [
				{ textKey: "pf_includes_pro", included: true },
				{ textKey: "pf_multistore", included: true },
				{ textKey: "pf_dashboards_advanced", included: true },
				{ textKey: "pf_bulk_messaging", included: true },
				{ textKey: "pf_ai_included_limited", included: true },
			],
		},
		{
			id: "personalizado",
			name: "Personalizado",
			taglineKey: "plan_custom_tagline",
			price: "0",
			implPrice: "Desde $1,500",
			badge: "pricing_badge_enterprise",
			accentHex: "#8b5cf6",
			accentDark: "#6d28d9",
			ctaKey: "plan_custom_cta",
			waText: "Plan%20Personalizado",
			priceOnRequest: true,
			limitValues: {
				users: "A medida",
				cajas: "A medida",
				branches: "A medida",
			},
			support: {
				responseKey: "support_custom_response",
				channelsKey: "support_custom_channels",
				includesKey: "support_custom_includes",
			},
			features: [
				{ textKey: "pf_industry_modules", included: true },
				{ textKey: "pf_integrations_custom", included: true },
				{ textKey: "pf_api", included: true },
				{ textKey: "pf_ai_by_usage", included: true },
				{ textKey: "pf_sla_agreed", included: true },
				{ textKey: "pf_account_manager", included: true },
			],
		},
	];

	const standardPlans = planDefs.filter((p) => !p.priceOnRequest);

	const toggleBg = {
		backgroundColor: isLight ? "#e8eef8" : "rgba(12,22,48,0.8)",
		borderColor: isLight ? "#c5d4e8" : "rgba(255,255,255,0.10)",
	};

	const secondaryBadgeStyle = isLight
		? {
				backgroundColor: "rgba(208,160,24,0.12)",
				border: "1px solid rgba(208,160,24,0.34)",
				iconColor: "#b37a00",
				textColor: "#996700",
			}
		: {
				backgroundColor: "rgba(250,204,21,0.12)",
				border: "1px solid rgba(250,204,21,0.40)",
				iconColor: "#facc15",
				textColor: "#fde047",
			};

	return (
		<section
			id="pricing"
			className="bg-navy-950 py-14 md:py-24 px-5 md:px-6"
			ref={sectionRef}
		>
			<div className="max-w-7xl mx-auto">
				{/* ── Header ─────────────────────────────────────────────── */}
				<div
					className="text-center mb-9 md:mb-12"
					style={{
						opacity: visible ? 1 : 0,
						transform: visible ? "translateY(0)" : "translateY(24px)",
						transition: "opacity 0.6s ease, transform 0.6s ease",
					}}
				>
					<span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
						{t("pricing_label")}
					</span>
					<h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
						{t("pricing_title_1")}{" "}
						<span className="text-electric">{t("pricing_title_accent")}</span>
					</h2>
					<p className="font-body text-white/60 text-sm md:text-base max-w-xl mx-auto mb-6 md:mb-8">
						{t("pricing_sub")}
					</p>

					{/* Billing system clarification */}
					<div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-6 w-full px-2">
						<div className="flex items-center gap-2 bg-electric/10 border border-electric/25 rounded-full px-4 py-2 w-full sm:w-auto justify-center">
							<div className="w-4 h-4 flex items-center justify-center text-electric flex-shrink-0">
								<i className="ri-bill-line text-sm"></i>
							</div>
							<span className="font-body text-xs font-semibold text-electric text-center leading-snug">
								{t("pricing_billing_note")}
							</span>
						</div>
						<div
							className="flex items-center gap-2 rounded-full px-4 py-2 w-full sm:w-auto justify-center"
							style={{
								backgroundColor: secondaryBadgeStyle.backgroundColor,
								border: secondaryBadgeStyle.border,
							}}
						>
							<div
								className="w-4 h-4 flex items-center justify-center flex-shrink-0"
								style={{ color: secondaryBadgeStyle.iconColor }}
							>
								<i className="ri-sparkling-line text-sm"></i>
							</div>
							<span
								className="font-body text-xs font-semibold text-center leading-snug"
								style={{ color: secondaryBadgeStyle.textColor }}
							>
								{t("pricing_other_services")}
							</span>
						</div>
					</div>

					{/* Monthly / Annual toggle */}
					<div className="flex justify-center">
						<div
							className="inline-flex items-center gap-1 rounded-full px-1 py-1 border w-full sm:w-auto justify-center max-w-xs"
							style={toggleBg}
						>
							<button
								onClick={() => setAnnual(false)}
								className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap touch-manipulation ${!annual ? "bg-electric text-navy-950" : ""}`}
								style={{
									color: annual
										? isLight
											? "rgba(10,22,40,0.50)"
											: "rgba(255,255,255,0.60)"
										: undefined,
								}}
							>
								{t("pricing_monthly")}
							</button>
							<button
								onClick={() => setAnnual(true)}
								className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-semibold font-body transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 touch-manipulation ${annual ? "bg-electric text-navy-950" : ""}`}
								style={{
									color: !annual
										? isLight
											? "rgba(10,22,40,0.50)"
											: "rgba(255,255,255,0.60)"
										: undefined,
								}}
							>
								{t("pricing_annual")}
								<span
									className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${annual ? "bg-navy-950/20 text-navy-950" : "bg-electric/20 text-electric"}`}
								>
									−$3.99
								</span>
							</button>
						</div>
					</div>
				</div>

				{/* ── 4 standard plan cards ──────────────────────────────── */}
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6 items-start">
					{standardPlans.map((plan, i) => (
						<PlanCard
							key={plan.id}
							plan={plan}
							price={getPrice(plan.price)}
							annual={annual}
							isLight={isLight}
							index={i}
							visible={visible}
							t={t}
							onSelectPlan={handleSelectPlan}
						/>
					))}
				</div>

				{/* ── Collapsible comparison table ───────────────────────── */}
				<div className="flex justify-center mt-9 md:mt-12">
					<button
						onClick={() => setShowCompare((v) => !v)}
						className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-body text-sm font-semibold cursor-pointer transition-all duration-200 hover:scale-[1.02]"
						style={{
							borderColor: isLight
								? "rgba(0,207,255,0.35)"
								: "rgba(0,207,255,0.30)",
							backgroundColor: isLight
								? "rgba(0,207,255,0.08)"
								: "rgba(0,207,255,0.10)",
							color: "#00cfff",
						}}
					>
						<i className="ri-table-line text-base"></i>
						{showCompare ? t("pricing_compare_hide") : t("pricing_compare_show")}
						<i
							className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${showCompare ? "rotate-180" : ""}`}
						></i>
					</button>
				</div>

				{showCompare && (
					<SimpleCompareTable
						planDefs={planDefs}
						isLight={isLight}
						visible={visible}
						t={t}
					/>
				)}

				{/* ── Important commercial notes ──────────────────────────── */}
				<ImportantNotes visible={visible} isLight={isLight} t={t} />
			</div>

			{modalPlan && (
				<PlanFormModal
					planName={modalPlan.name}
					planColor={modalPlan.color}
					onClose={() => setModalPlan(null)}
				/>
			)}
		</section>
	);
}

// ─── Support block (shown on every plan card) ─────────────────────────────────
interface SupportBlockProps {
	support: PlanDef["support"];
	accent: string;
	isLight: boolean;
	t: (key: string) => string;
}

function SupportBlock({ support, accent, isLight, t }: SupportBlockProps) {
	return (
		<div
			className="rounded-xl p-3 mb-4"
			style={{
				backgroundColor: accent + (isLight ? "0d" : "12"),
				border: `1px solid ${accent}22`,
			}}
		>
			<div className="flex items-center gap-1.5 mb-2">
				<div
					className="w-4 h-4 flex items-center justify-center flex-shrink-0"
					style={{ color: accent }}
				>
					<i className="ri-customer-service-2-line text-sm"></i>
				</div>
				<span
					className="font-body text-[11px] font-bold uppercase tracking-wider"
					style={{ color: accent }}
				>
					{t("support_block_title")}
				</span>
			</div>
			<div className="flex flex-col gap-1.5">
				{SUPPORT_ROWS.map((row) => (
					<div key={row.field} className="flex items-start gap-1.5">
						<div
							className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-[1px]"
							style={{ color: accent }}
						>
							<i className={`${row.icon} text-[11px]`}></i>
						</div>
						<p
							className="font-body text-[11px] leading-snug"
							style={{
								color: isLight
									? "rgba(10,22,40,0.60)"
									: "rgba(255,255,255,0.55)",
							}}
						>
							<span
								className="font-semibold"
								style={{ color: isLight ? "#0a1628" : "#ffffff" }}
							>
								{t(row.labelKey)}:
							</span>{" "}
							{t(support[row.field])}
						</p>
					</div>
				))}
			</div>
		</div>
	);
}

// ─── Plan Card ────────────────────────────────────────────────────────────────
interface PlanCardProps {
	plan: PlanDef;
	price: string;
	annual: boolean;
	isLight: boolean;
	index: number;
	visible: boolean;
	t: (key: string) => string;
	onSelectPlan: (plan: PlanDef, color: string) => void;
}

function PlanCard({
	plan,
	price,
	annual,
	isLight,
	index,
	visible,
	t,
	onSelectPlan,
}: PlanCardProps) {
	const [hovered, setHovered] = useState(false);
	const [showFeatures, setShowFeatures] = useState(false);
	const recommended = plan.badge === "pricing_badge_complete";
	const accent = plan.accentHex;
	const delay = index * 0.08 + 0.15;
	const included = plan.features.filter((f) => f.included).slice(0, 6);

	const cardBg = isLight
		? recommended
			? `linear-gradient(165deg, ${accent}16 0%, #ffffff 55%)`
			: "#ffffff"
		: recommended
			? `linear-gradient(165deg, ${accent}26 0%, rgba(8,16,38,0.98) 60%)`
			: "rgba(12,22,48,0.92)";

	const borderColor = recommended
		? accent + (isLight ? "aa" : "80")
		: hovered
			? accent + (isLight ? "88" : "55")
			: isLight
				? accent + "33"
				: "rgba(255,255,255,0.08)";

	const shadow = isLight
		? hovered || recommended
			? `0 10px 34px ${accent}22, 0 4px 14px rgba(0,0,0,0.06)`
			: "0 2px 12px rgba(0,0,0,0.06)"
		: recommended
			? `0 0 34px ${accent}33, 0 20px 50px rgba(0,0,0,0.45)`
			: hovered
				? `0 0 28px ${accent}22, 0 16px 40px rgba(0,0,0,0.4)`
				: "none";

	return (
		<div
			className="relative flex flex-col rounded-2xl border overflow-hidden md:min-h-[498px]"
			style={{
				background: cardBg,
				borderColor,
				boxShadow: shadow,
				transform: visible
					? hovered
						? "translateY(-8px)"
						: recommended
							? "translateY(-6px)"
							: "translateY(0)"
					: "translateY(28px)",
				opacity: visible ? 1 : 0,
				transition: `opacity 0.5s ease ${delay}s, transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, border-color 0.3s ease`,
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			{/* Top accent bar */}
			<div
				className="absolute top-0 left-0 right-0 h-[3px]"
				style={{
					background: `linear-gradient(to right, ${accent}, ${accent}55, transparent)`,
					opacity: recommended || hovered ? 1 : 0.5,
				}}
			/>

			<div className="flex flex-col flex-1 p-5">
				{/* Badge */}
				{plan.badge ? (
					<div
						className="self-start inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full font-body mb-3 border"
						style={{
							backgroundColor: accent + "1f",
							borderColor: accent + "4d",
							color: accent,
						}}
					>
						<div
							className="w-1.5 h-1.5 rounded-full animate-pulse"
							style={{ backgroundColor: accent }}
						/>
						{t(plan.badge)}
					</div>
				) : (
					<div className="h-8 mb-3" />
				)}

				{/* Name */}
				<h3
					className="font-heading text-2xl font-extrabold leading-none"
					style={{ color: accent }}
				>
					{plan.name}
				</h3>

				{/* Recommended caption (only for the highlighted plan) */}
				{recommended && (
					<p
						className="font-body text-[11px] font-semibold mt-1.5"
						style={{ color: accent }}
					>
						{t("pricing_recommended")}
					</p>
				)}

				{/* Summary */}
				<p
					className="font-body text-xs mt-2 mb-4 leading-snug"
					style={{
						color: isLight ? "rgba(10,22,40,0.55)" : "rgba(255,255,255,0.50)",
					}}
				>
					{t(plan.taglineKey)}
				</p>

				{/* Price */}
				<div className="flex items-end gap-1">
					<AnimatedPrice price={price} isLight={isLight} />
					<span
						className="font-body text-xs mb-1.5"
						style={{
							color: isLight
								? "rgba(10,22,40,0.50)"
								: "rgba(255,255,255,0.50)",
						}}
					>
						{annual ? t("pricing_usd_year") : t("pricing_usd_month")}
					</span>
				</div>

				{/* Implementation */}
				<div className="flex items-center gap-1.5 mt-1.5 mb-2">
					<div
						className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"
						style={{ color: accent }}
					>
						<i className="ri-add-circle-line text-xs"></i>
					</div>
					<span
						className="font-body text-xs"
						style={{
							color: isLight
								? "rgba(10,22,40,0.50)"
								: "rgba(255,255,255,0.45)",
						}}
					>
						<strong
							style={{
								color: isLight
									? "rgba(10,22,40,0.75)"
									: "rgba(255,255,255,0.70)",
							}}
						>
							{plan.implPrice}
						</strong>{" "}
						{t("pricing_impl")}
					</span>
				</div>

				{/* Annual saving */}
				{annual ? (
					<div
						className="inline-flex self-start items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full font-body mb-4"
						style={{
							backgroundColor: accent + "18",
							color: accent,
						}}
					>
						<i className="ri-price-tag-3-line text-xs"></i>
						{t("pricing_save")} $47.88 USD/{t("pricing_year")}
					</div>
				) : (
					<div className="mb-4" />
				)}

				{/* Limits */}
				<div className="grid grid-cols-3 gap-2 mb-5">
					{LIMIT_META.map((l) => (
						<div
							key={l.field}
							className="flex flex-col items-center text-center gap-1 rounded-lg px-2 py-2.5"
							style={{
								backgroundColor: accent + "10",
								border: `1px solid ${accent}22`,
							}}
						>
							<div
								className="w-4 h-4 flex items-center justify-center flex-shrink-0"
								style={{ color: accent }}
							>
								<i className={`${l.icon} text-sm`}></i>
							</div>
							<p
								className="font-heading text-sm font-bold leading-none"
								style={{ color: isLight ? "#0a1628" : "#ffffff" }}
							>
								{plan.limitValues[l.field]}
							</p>
							<p
								className="font-body text-[10px] leading-tight"
								style={{
									color: isLight
										? "rgba(10,22,40,0.45)"
										: "rgba(255,255,255,0.45)",
								}}
							>
								{t(l.labelKey)}
							</p>
						</div>
					))}
				</div>

				{/* Divider */}
				<div
					className="border-t mb-4"
					style={{
						borderColor: isLight
							? "rgba(10,22,40,0.08)"
							: "rgba(255,255,255,0.08)",
					}}
				/>

				{/* Collapsible "what's included" */}
				<button
					onClick={() => setShowFeatures((v) => !v)}
					className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 mb-3 font-body text-xs font-semibold cursor-pointer transition-colors"
					style={{
						backgroundColor: accent + "12",
						color: accent,
						border: `1px solid ${accent}2a`,
					}}
				>
					<span className="inline-flex items-center gap-1.5">
						<i className="ri-list-check-2 text-sm"></i>
						{showFeatures ? t("pricing_view_less") : t("pricing_whats_included")}
					</span>
					<i
						className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${showFeatures ? "rotate-180" : ""}`}
					></i>
				</button>

				<div
					style={{
						maxHeight: showFeatures ? 520 : 0,
						opacity: showFeatures ? 1 : 0,
						overflow: "hidden",
						transition: "max-height 0.4s ease, opacity 0.3s ease",
					}}
				>
					{/* Benefits */}
					<ul className="space-y-2 mb-4">
						{included.map((f) => (
							<li
								key={f.textKey}
								className="flex items-start gap-2 text-xs font-body"
								style={{
									color: isLight
										? "rgba(10,22,40,0.80)"
										: "rgba(255,255,255,0.80)",
								}}
							>
								<div
									className="flex-shrink-0 mt-[1px]"
									style={{ color: accent, fontSize: "14px", lineHeight: 1 }}
								>
									<i className="ri-checkbox-circle-fill" />
								</div>
								<span style={{ lineHeight: "1.45" }}>{t(f.textKey)}</span>
							</li>
						))}
					</ul>

					{/* Support block */}
					<SupportBlock
						support={plan.support}
						accent={accent}
						isLight={isLight}
						t={t}
					/>
				</div>

				{/* Spacer keeps CTA aligned at the bottom */}
				<div className="flex-1" />

				{/* CTA */}
				<button
					onClick={() => onSelectPlan(plan, accent)}
					className="w-full flex items-center justify-center gap-2 font-bold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap font-body transform hover:scale-[1.03] active:scale-[0.97]"
					style={{
						background: `linear-gradient(135deg, ${accent}, ${plan.accentDark})`,
						color: "#ffffff",
						boxShadow: `0 6px 18px ${accent}30`,
					}}
				>
					{t(plan.ctaKey)}
					<div className="w-4 h-4 flex items-center justify-center">
						<i className="ri-arrow-right-line text-sm"></i>
					</div>
				</button>
			</div>
		</div>
	);
}

// ─── Simple comparison table ──────────────────────────────────────────────────
interface SimpleCompareTableProps {
	planDefs: PlanDef[];
	isLight: boolean;
	visible: boolean;
	t: (key: string) => string;
}

function SimpleCompareTable({
	planDefs,
	isLight,
	visible,
	t,
}: SimpleCompareTableProps) {
	const tableBorder = isLight
		? "rgba(10,22,40,0.12)"
		: "rgba(255,255,255,0.08)";
	const rowBorder = isLight ? "rgba(10,22,40,0.07)" : "rgba(255,255,255,0.06)";
	const headerBg = isLight ? "#eef3fb" : "rgba(12,22,48,0.95)";
	const labelBg = isLight ? "#f5f8ff" : "rgba(12,22,48,0.45)";
	const labelColor = isLight
		? "rgba(10,22,40,0.70)"
		: "rgba(255,255,255,0.62)";
	const valueColor = isLight ? "#0a1628" : "#ffffff";
	const mutedColor = isLight
		? "rgba(10,22,40,0.28)"
		: "rgba(255,255,255,0.28)";

	const renderCell = (cell: CompareCell, accent: string) => {
		if (cell === "check") {
			return (
				<div className="flex justify-center">
					<div
						className="w-5 h-5 flex items-center justify-center rounded-full"
						style={{ backgroundColor: accent + "22", color: accent }}
					>
						<i className="ri-check-line text-xs font-bold"></i>
					</div>
				</div>
			);
		}
		if (cell === "addon") {
			return (
				<span
					className="inline-block font-body text-[10px] font-bold px-2 py-0.5 rounded-full"
					style={{
						backgroundColor: isLight
							? "rgba(208,160,24,0.14)"
							: "rgba(250,204,21,0.14)",
						color: isLight ? "#a06b00" : "#facc15",
						border: isLight
							? "1px solid rgba(208,160,24,0.30)"
							: "1px solid rgba(250,204,21,0.30)",
					}}
				>
					{t("compare_addon_label")}
				</span>
			);
		}
		return (
			<span className="font-body text-sm" style={{ color: mutedColor }}>
				—
			</span>
		);
	};

	return (
		<div
			className="mt-14"
			style={{
				opacity: visible ? 1 : 0,
				transform: visible ? "translateY(0)" : "translateY(20px)",
				transition: "opacity 0.6s ease 0.6s, transform 0.6s ease 0.6s",
			}}
		>
			<h3 className="font-heading text-xl md:text-2xl font-bold text-white text-center mb-6">
				{t("compare_simple_title")}
			</h3>

			<div
				className="overflow-x-auto rounded-2xl border"
				style={{ borderColor: tableBorder }}
			>
				<table
					className="w-full min-w-[760px]"
					style={{ borderCollapse: "collapse" }}
				>
					<thead>
						<tr>
							<th
								className="text-left p-4 pl-5 font-body text-xs font-semibold uppercase tracking-wider w-52"
								style={{ backgroundColor: headerBg, color: labelColor }}
							>
								{t("compare_feature_col")}
							</th>
							{planDefs.map((plan) => {
								const highlight =
									plan.badge === "pricing_badge_complete" ||
									plan.priceOnRequest;
								return (
									<th
										key={plan.id}
										className="p-4 text-center"
										style={{
											backgroundColor: highlight
												? plan.accentHex + (isLight ? "14" : "12")
												: headerBg,
											borderLeft: `1px solid ${rowBorder}`,
											minWidth: "112px",
										}}
									>
										<div
											className="font-heading text-sm md:text-base font-extrabold"
											style={{ color: plan.accentHex }}
										>
											{plan.name}
										</div>
										{!plan.priceOnRequest ? (
											<div
												className="font-heading text-xs font-bold mt-1"
												style={{ color: valueColor }}
											>
												${plan.price}
												<span
													className="font-body font-normal"
													style={{ color: labelColor }}
												>
													/{t("pricing_monthly").toLowerCase()}
												</span>
											</div>
										) : (
											<div
												className="font-heading text-xs font-bold mt-1"
												style={{ color: plan.accentHex }}
											>
												{t("plan_custom_price")}
											</div>
										)}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{/* Limit rows */}
						{LIMIT_META.map((row, i) => (
							<tr
								key={row.field}
								style={{ borderTop: `1px solid ${rowBorder}` }}
							>
								<td
									className="p-3.5 pl-5 font-body text-xs font-semibold"
									style={{ backgroundColor: labelBg, color: labelColor }}
								>
									{t(row.labelKey)}
								</td>
								{planDefs.map((plan) => (
									<td
										key={plan.id}
										className="p-3.5 text-center"
										style={{
											borderLeft: `1px solid ${rowBorder}`,
											backgroundColor:
												i % 2 === 0
													? isLight
														? "rgba(10,22,40,0.01)"
														: "rgba(255,255,255,0.01)"
													: "transparent",
										}}
									>
										<span
											className="font-body text-xs font-bold px-2 py-0.5 rounded-full inline-block"
											style={{
												backgroundColor: plan.accentHex + "15",
												color: plan.priceOnRequest
													? plan.accentHex
													: valueColor,
											}}
										>
											{plan.limitValues[row.field]}
										</span>
									</td>
								))}
							</tr>
						))}
						{/* Feature rows */}
						{COMPARE_FEATURE_ROWS.map((row, i) => (
							<tr
								key={row.labelKey}
								style={{ borderTop: `1px solid ${rowBorder}` }}
							>
								<td
									className="p-3.5 pl-5 font-body text-xs"
									style={{ backgroundColor: labelBg, color: labelColor }}
								>
									{t(row.labelKey)}
								</td>
								{planDefs.map((plan) => (
									<td
										key={plan.id}
										className="p-3.5 text-center"
										style={{
											borderLeft: `1px solid ${rowBorder}`,
											backgroundColor:
												i % 2 === 0
													? isLight
														? "rgba(10,22,40,0.01)"
														: "rgba(255,255,255,0.01)"
													: "transparent",
										}}
									>
										{renderCell(row.cells[plan.id] ?? "dash", plan.accentHex)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Legend */}
			<div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-4">
				<div className="flex items-center gap-1.5">
					<div className="w-4 h-4 flex items-center justify-center rounded-full bg-electric/20 text-electric">
						<i className="ri-check-line text-[10px] font-bold"></i>
					</div>
					<span className="font-body text-xs text-white/50">
						{t("compare_legend_included")}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<span
						className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full"
						style={{
							backgroundColor: "rgba(250,204,21,0.14)",
							color: "#facc15",
							border: "1px solid rgba(250,204,21,0.30)",
						}}
					>
						{t("compare_addon_label")}
					</span>
					<span className="font-body text-xs text-white/50">
						{t("compare_legend_addon")}
					</span>
				</div>
				<div className="flex items-center gap-1.5">
					<span className="font-body text-sm text-white/30">—</span>
					<span className="font-body text-xs text-white/50">
						{t("compare_legend_no")}
					</span>
				</div>
			</div>
		</div>
	);
}

// ─── Important commercial notes ───────────────────────────────────────────────
interface ImportantNotesProps {
	visible: boolean;
	isLight: boolean;
	t: (key: string) => string;
}

function ImportantNotes({ visible, isLight, t }: ImportantNotesProps) {
	const noteKeys = [
		"pricing_imp_note_usd",
		"pricing_imp_note_dop",
		"pricing_imp_note_impl",
		"pricing_imp_note_extras",
		"pricing_imp_note_fel",
		"pricing_imp_note_custom",
		"pricing_imp_note_evolution",
	];

	const textColor = isLight ? "rgba(10,22,40,0.52)" : "rgba(255,255,255,0.42)";
	const titleColor = isLight ? "rgba(10,22,40,0.60)" : "rgba(255,255,255,0.55)";
	const bulletColor = isLight
		? "rgba(10,22,40,0.30)"
		: "rgba(255,255,255,0.28)";

	return (
		<div
			className="mt-12 max-w-3xl mx-auto rounded-2xl border px-6 py-6 md:px-8 md:py-7"
			style={{
				borderColor: isLight
					? "rgba(10,22,40,0.10)"
					: "rgba(255,255,255,0.07)",
				backgroundColor: isLight ? "#f7f9fd" : "rgba(12,22,48,0.35)",
				opacity: visible ? 1 : 0,
				transform: visible ? "translateY(0)" : "translateY(16px)",
				transition: "opacity 0.6s ease 1.1s, transform 0.6s ease 1.1s",
			}}
		>
			<div className="flex items-center gap-2 mb-3.5">
				<div
					className="w-4 h-4 flex items-center justify-center flex-shrink-0"
					style={{ color: isLight ? "rgba(10,22,40,0.45)" : "rgba(255,255,255,0.40)" }}
				>
					<i className="ri-information-line text-sm"></i>
				</div>
				<h4
					className="font-body text-[13px] font-semibold uppercase tracking-wider"
					style={{ color: titleColor }}
				>
					{t("pricing_notes_title")}
				</h4>
			</div>
			<ul className="flex flex-col gap-2">
				{noteKeys.map((key) => (
					<li
						key={key}
						className="flex items-start gap-2.5 font-body text-[11.5px] md:text-xs leading-relaxed"
						style={{ color: textColor }}
					>
						<span
							className="flex-shrink-0 mt-[6px] w-1 h-1 rounded-full"
							style={{ backgroundColor: bulletColor }}
						/>
						<span>{t(key)}</span>
					</li>
				))}
			</ul>
		</div>
	);
}
